import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Server, 
  Database, 
  Zap, 
  Users,
  Activity,
  Clock,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface HealthMetric {
  name: string;
  status: 'healthy' | 'warning' | 'critical';
  value: string;
  detail?: string;
}

export function SystemHealthDashboard() {
  const [metrics, setMetrics] = useState<HealthMetric[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkHealth = async () => {
      setLoading(true);
      
      const [usersRes, jobsRes, alertsRes, eventsRes] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('agent_job_runs').select('status').eq('status', 'running'),
        supabase.from('alerts').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('agent_events').select('id', { count: 'exact', head: true }),
      ]);

      const activeJobs = jobsRes.data?.length || 0;
      const pendingAlerts = alertsRes.count || 0;

      const healthMetrics: HealthMetric[] = [
        {
          name: 'Database',
          status: 'healthy',
          value: 'Connected',
          detail: 'All queries responding normally',
        },
        {
          name: 'Edge Functions',
          status: 'healthy',
          value: '13 Active',
          detail: 'All functions deployed',
        },
        {
          name: 'Active Users',
          status: 'healthy',
          value: (usersRes.count || 0).toString(),
          detail: 'Registered accounts',
        },
        {
          name: 'Running Jobs',
          status: activeJobs > 5 ? 'warning' : 'healthy',
          value: activeJobs.toString(),
          detail: activeJobs > 0 ? 'Processing...' : 'Idle',
        },
        {
          name: 'Pending Alerts',
          status: pendingAlerts > 50 ? 'warning' : 'healthy',
          value: pendingAlerts.toString(),
          detail: 'Awaiting review',
        },
        {
          name: 'Agent Events',
          status: 'healthy',
          value: (eventsRes.count || 0).toString(),
          detail: 'Total logged events',
        },
      ];

      setMetrics(healthMetrics);
      setLoading(false);
    };

    checkHealth();
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const overallHealth = metrics.some(m => m.status === 'critical')
    ? 'critical'
    : metrics.some(m => m.status === 'warning')
    ? 'warning'
    : 'healthy';

  const statusIcons = {
    healthy: CheckCircle,
    warning: AlertTriangle,
    critical: AlertTriangle,
  };

  const statusColors = {
    healthy: 'text-success',
    warning: 'text-warning',
    critical: 'text-destructive',
  };

  return (
    <Card className="rounded-3xl">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Server className="h-5 w-5 text-primary" />
            System Health
          </CardTitle>
          <Badge
            variant="outline"
            className={cn(
              'rounded-xl capitalize',
              overallHealth === 'healthy' && 'border-success text-success',
              overallHealth === 'warning' && 'border-warning text-warning',
              overallHealth === 'critical' && 'border-destructive text-destructive'
            )}
          >
            {loading ? 'Checking...' : overallHealth}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-12 rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {metrics.map((metric) => {
              const StatusIcon = statusIcons[metric.status];
              return (
                <div
                  key={metric.name}
                  className="flex items-center justify-between p-3 rounded-xl bg-surface hover:bg-surface-elevated transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <StatusIcon className={cn('h-4 w-4', statusColors[metric.status])} />
                    <div>
                      <p className="text-sm font-medium text-foreground">{metric.name}</p>
                      {metric.detail && (
                        <p className="text-xs text-muted-foreground">{metric.detail}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-foreground">{metric.value}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Cron Jobs Status */}
        <div className="mt-6 pt-4 border-t border-border">
          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Scheduled Jobs
          </h4>
          <div className="space-y-2 text-sm">
            {[
              { name: 'Hourly Agent Scan', schedule: 'Every hour', status: 'active' },
              { name: 'Daily Notifications', schedule: '9:00 AM IST', status: 'active' },
              { name: 'Pattern Analysis', schedule: '4:00 AM UTC', status: 'active' },
              { name: 'Learning Loop', schedule: '5:00 AM UTC', status: 'active' },
              { name: 'Proactive Outreach', schedule: '10:00 AM IST', status: 'active' },
            ].map((job) => (
              <div key={job.name} className="flex items-center justify-between p-2 rounded-lg hover:bg-surface">
                <span className="text-muted-foreground">{job.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{job.schedule}</span>
                  <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
